// src/services/baseQuery.ts

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { RootState } from '../store'
import { logout, setCredentials } from '../store/slices/authSlice'
import { LoginResponse } from '../types/api/responses'
import { jwtDecode } from 'jwt-decode'
// Buffer time in seconds before token expiry to trigger refresh
const TOKEN_REFRESH_BUFFER = 300

// List of endpoints that should not receive the Authorization header
const AUTH_HEADER_EXCLUSION_LIST = ['refresh']

interface JwtPayload {
  exp: number
  sub: string // Add sub claim which typically contains the user ID
}

const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const decodedToken = jwtDecode<JwtPayload>(token)
    const currentTime = Math.floor(Date.now() / 1000)
    return decodedToken.exp - currentTime <= TOKEN_REFRESH_BUFFER
  } catch (error) {
    console.error('Error decoding token:', error)
    return true // Assume token needs refresh if we can't decode it
  }
}

export const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_ENDPOINT}/v1`,
  prepareHeaders: (headers, { getState, endpoint }) => {
    // Check if the current endpoint is in the exclusion list
    if (!AUTH_HEADER_EXCLUSION_LIST.includes(endpoint)) {
      const token = (getState() as RootState).auth.accessToken
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
        // Add userid header by decoding the JWT token
        try {
          const decodedToken = jwtDecode<JwtPayload>(token)
          if (decodedToken.sub) {
            headers.set('userid', decodedToken.sub)
          }
        } catch (error) {
          console.error('Error decoding token for userid:', error)
        }
      }
    }
    headers.set('Cache-Control', 'no-cache')

    // Use the same domain configuration as CSP
    // const apiDomain = getApiDomain()
    return headers
  },
  credentials: 'include',
})

// Create a separate base query instance for the refresh token endpoint
// This instance specifically avoids adding the Authorization header
// and removes the /v1 from the URL
const refreshBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_ENDPOINT}`,
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json')
    return headers
  },
})

// Create a public base query that never adds auth headers
export const publicBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_ENDPOINT}/v1`,
  prepareHeaders: (headers) => {
    headers.set('Cache-Control', 'no-cache')
    return headers
  },
  // Remove credentials to ensure no auth cookies are sent
})

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let state = api.getState() as RootState

  // First try to use tokens from Redux state
  let accessToken = state.auth.accessToken
  let refreshToken = state.auth.refreshToken

  // If either token is missing from state, try to get from localStorage
  if (!accessToken || !refreshToken) {
    const storedAccessToken = localStorage.getItem('access_token')
    const storedRefreshToken = localStorage.getItem('refresh_token')

    if (storedAccessToken && storedRefreshToken) {
      accessToken = storedAccessToken
      refreshToken = storedRefreshToken
      // Restore tokens to Redux state
      api.dispatch(
        setCredentials({
          access_token: storedAccessToken,
          refresh_token: storedRefreshToken,
        })
      )
      // Update state reference
      state = api.getState() as RootState
    }
  }

  // Only proceed if we have both tokens
  if (accessToken && refreshToken) {
    try {
      // Check if token is expiring soon before making the request
      if (isTokenExpiringSoon(accessToken)) {
        try {
          console.log('Token expiring soon, refreshing proactively')
          const refreshResult = await refreshBaseQuery(
            {
              url: `/refresh/${refreshToken}`,
              method: 'GET',
            },
            api,
            extraOptions
          )

          if (refreshResult.data) {
            const { access_token: newAccessToken, refresh_token: newRefreshToken } =
              refreshResult.data as LoginResponse
            if (newAccessToken) {
              api.dispatch(
                setCredentials({
                  access_token: newAccessToken,
                  refresh_token: newRefreshToken || refreshToken,
                })
              )
              // Update accessToken for the upcoming request
              accessToken = newAccessToken
            }
          }
        } catch (error) {
          console.error('Proactive token refresh failed:', error)
          // Continue with the original request even if proactive refresh fails
        }
      }

      let result = await baseQuery(args, api, extraOptions)

      // Handle 401 or fetch errors that might need token refresh
      if (result.error && (result.error.status === 401 || result.error.status === 'FETCH_ERROR')) {
        try {
          // Try to refresh the token
          console.log('refreshing token')
          const refreshResult = await refreshBaseQuery(
            {
              url: `/refresh/${refreshToken}`,
              method: 'GET',
            },
            api,
            extraOptions
          )

          if (refreshResult.data) {
            const { access_token: newAccessToken, refresh_token: newRefreshToken } =
              refreshResult.data as LoginResponse

            if (newAccessToken) {
              // Store the new tokens
              api.dispatch(
                setCredentials({
                  access_token: newAccessToken,
                  refresh_token: newRefreshToken || refreshToken,
                })
              )
              // Retry the original request with new token
              console.log('retrying request')
              result = await baseQuery(args, api, extraOptions)
            } else {
              throw new Error('No access token in refresh response')
            }
          } else {
            throw new Error('Failed to refresh token')
          }
        } catch (error) {
          console.error('Token refresh failed:', error)
          api.dispatch(logout())
          return { error: { status: 401, data: 'Token refresh failed' } }
        }
      }
      return result
    } catch (error) {
      console.error('Request failed:', error)
      return { error: { status: 500, data: 'Request failed' } }
    }
  }

  // If we get here, we don't have valid tokens
  api.dispatch(logout())
  return { error: { status: 401, data: 'No valid auth tokens' } }
}

export default baseQueryWithReauth
